import React from 'react';
import { motion } from 'framer-motion';
import {
  Award, ShieldAlert, Cpu, HeartHandshake, Zap, CheckCircle
} from 'lucide-react';

const reasons = [
  {
    icon: Award,
    title: '25+ Years Experience',
    desc: 'Founded in 1999. Over two decades of solving complex IT bottlenecks and implementing premium enterprise systems.',
    color: 'text-cyan-400',
    glowColor: 'group-hover:shadow-cyan-500/10'
  },
  {
    icon: ShieldAlert,
    title: 'Certified Engineers',
    desc: 'Our team holds premium certifications from Cisco, Fortinet, Microsoft, AWS, and VMware to ensure high-grade configurations.',
    color: 'text-blue-500',
    glowColor: 'group-hover:shadow-blue-500/10'
  },
  {
    icon: Cpu,
    title: 'Enterprise Solutions',
    desc: 'We engineer bespoke, secure, and robust technology blueprints designed for the long-term scalability of your business operations.',
    color: 'text-indigo-400',
    glowColor: 'group-hover:shadow-indigo-500/10'
  },
  {
    icon: Zap,
    title: 'Rapid Response Support',
    desc: 'Critical response helpdesk and dedicated engineers on standby to resolve infrastructure issues and ensure maximum uptime.',
    color: 'text-rose-500',
    glowColor: 'group-hover:shadow-rose-500/10'
  },
  {
    icon: HeartHandshake,
    title: 'Trusted Tech Partners',
    desc: 'Direct channels with HP, Lenovo, Dell, IBM, Cisco, Juniper, and Symantec to source top-tier systems at competitive rates.',
    color: 'text-purple-400',
    glowColor: 'group-hover:shadow-purple-500/10'
  },
  {
    icon: CheckCircle,
    title: 'Customer Satisfaction',
    desc: 'Over 1,129 corporate clients across Gujarat and India rely on our services to power their operational workflows.',
    color: 'text-emerald-400',
    glowColor: 'group-hover:shadow-emerald-500/10'
  }
];

export default function WhyChooseUs() {
  return (
    <section className="relative py-24 border-t border-white/5 bg-navy-deep/20">
      <div className="max-w-7xl mx-auto px-6 md:px-8">

        {/* Title */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-cyan/10 border border-brand-cyan/20 text-brand-cyan text-xs font-bold uppercase tracking-widest mb-6"
          >
            <span>The Advance IT Advantage</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-display font-black text-4xl md:text-5xl text-white tracking-tight mb-4"
          >
            Why Industry Leaders <span className="text-gradient">Choose Us</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="font-sans text-lg text-slate-400"
          >
            Providing security, speed, and engineering precision for enterprise environments since 1999.
          </motion.p>
        </div>

        {/* Reason Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, idx) => {
            const Icon = reason.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                className={`group relative rounded-2xl glass-panel p-8 glass-panel-hover flex flex-col items-start text-left hover:-translate-y-1.5 duration-300 ${reason.glowColor}`}
              >

                {/* Visual Icon Box */}
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-6 group-hover:border-brand-cyan/30 transition-colors duration-300">
                  <Icon size={22} className={`${reason.color} group-hover:scale-110 transition-transform duration-300`} />
                </div>

                <h3 className="font-display font-bold text-lg text-white mb-3 group-hover:text-brand-cyan transition-colors duration-300">
                  {reason.title}
                </h3>

                <p className="font-sans text-xs text-slate-400 leading-relaxed">
                  {reason.desc}
                </p>

              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
